import { ChartInfoKind } from '@frontend/chart/types';
import { CommonMessage } from '@frontend/lang';
import { FormattedMessage, useIntl } from 'react-intl';

import { PlatformObjectBadgeMapper } from '../../../modal/platform-object-badge/platform-object-badge-mapper.component';
import { StatisticsCard } from '../../shared';
import { useVendingProductsNeedingRefillListCard } from './use-vending-products-needing-refill-list-card';

export interface VendingProductsNeedingRefillListCardProps {
    accountId: string;
}

export const VendingProductsNeedingRefillListCard = (props: VendingProductsNeedingRefillListCardProps) => {
    const { formatMessage } = useIntl();
    const { items, isLoading } = useVendingProductsNeedingRefillListCard(props);

    const fillHeader = formatMessage({
        id: 'platform.statistics.vending.vending-products-needing-refill-list-card.fill',
        description: 'Column header for the current fill percentage in the vending refill priority list',
        defaultMessage: 'Fill'
    });

    return (
        <StatisticsCard
            infoKind={ChartInfoKind.PRODUCTS_NEEDING_REFILL_LIST}
            title={
                <FormattedMessage
                    id='platform.statistics.vending.vending-products-needing-refill-list-card.title'
                    description='Title of the vending dashboard list showing products that most urgently need replenishment based on fill percentage'
                    defaultMessage='Needs replenishment'
                />
            }
            subtitle={
                <FormattedMessage
                    id='platform.statistics.vending.vending-products-needing-refill-list-card.subtitle'
                    description='Subtitle indicating the refill priority list shows the most urgent products below minimum stock'
                    defaultMessage='Below minimum'
                />
            }
            isLoading={isLoading}
            hasData={items.length > 0}>
            <div className='min-h-0 flex-1 overflow-y-auto'>
                <div className='border-border-default text-foreground-default flex items-start gap-4 border-b text-base font-medium'>
                    <div className='w-full px-4 py-3'>{CommonMessage.OBJECTS.PRODUCT.DEFAULT}</div>
                    <div className='w-[140px] shrink-0 px-4 py-3'>{CommonMessage.OBJECTS.SPOT.DEFAULT}</div>
                    <div className='w-[88px] shrink-0 px-4 py-3 text-right'>{fillHeader}</div>
                </div>
                {items.map((item) => (
                    <div
                        key={item.id}
                        className='border-border-default flex h-11 items-center gap-4 border-b'>
                        <div className='text-foreground-default flex w-full min-w-0 items-center px-4 text-sm font-medium'>
                            <PlatformObjectBadgeMapper
                                id={item.product_id}
                                type='product_id'
                            />
                        </div>
                        <div className='flex w-[140px] shrink-0 items-center px-4'>
                            <PlatformObjectBadgeMapper
                                id={item.spot_id}
                                type='spot_id'
                            />
                        </div>
                        <div className='flex w-[88px] shrink-0 justify-end pe-4'>
                            <span className='text-background-surface bg-status-warning inline-flex min-w-8 items-center justify-center rounded-full px-2 py-0.5 text-sm font-semibold'>
                                {Math.round(item.filled_percentage)}%
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </StatisticsCard>
    );
};
